import { 
  Box, 
  VStack, 
  Text, 
  Button, 
  HStack, 
  Badge, 
  Heading, 
  Divider, 
  Flex,
  Icon
} from "@chakra-ui/react";
import { IoSparkles } from "react-icons/io5";
import { FiTrendingUp, FiDollarSign, FiArrowRight } from "react-icons/fi";
import { useState, useCallback } from "react";
import { useConfirmDialog } from "components/confirmModal";
import { SuggestionProps, CampaignGroup } from "./types";

interface SuggestionsPreviewProps {
  suggestions: SuggestionProps[];
  onApply: (id: number) => Promise<void>;
  onClose: () => void;
  onOpenAllSuggestions: () => void;
}

const SuggestionsPreview = ({
  suggestions,
  onApply,
  onClose,
  onOpenAllSuggestions
}: SuggestionsPreviewProps) => {
  const [isOpening, setIsOpening] = useState(false);
  const { ConfirmDialog } = useConfirmDialog();

  const suggestionsByCampaign: { [key: string]: CampaignGroup } = suggestions.reduce((acc: { [key: string]: CampaignGroup }, suggestion) => {
    const campaignId = suggestion.campaign.id;
    if (!acc[campaignId]) {
      acc[campaignId] = {
        name: suggestion.campaign.name,
        suggestions: []
      };
    }
    acc[campaignId].suggestions.push(suggestion);
    return acc;
  }, {});

  const handleApplyAll = useCallback(() => {
    setIsOpening(true);
    
    onClose();
    
    onOpenAllSuggestions();
    
    setTimeout(() => {
      setIsOpening(false);
    }, 300);
  }, [onClose, onOpenAllSuggestions]);

  return (
    <Box 
      p={3} 
      bg="white" 
      borderRadius="md" 
      boxShadow="0 3px 10px rgba(0,0,0,0.2)" 
      maxWidth="380px"
      minWidth="280px"
    >
      <VStack spacing={3} align="stretch">
        <Heading size="sm" color="brand.700" display="flex" alignItems="center">
          <Icon as={IoSparkles} mr={2} color="brand.500" />
          Campaign Improvements Available
        </Heading>
        
        <Box maxH="350px" overflowY="auto">
          <VStack spacing={2} align="stretch">
            {Object.values(suggestionsByCampaign).map((campaign: CampaignGroup) => (
              <Box 
                key={campaign.name} 
                bg="gray.50" 
                p={2} 
                borderRadius="md"
              >
                <Text fontWeight="bold" fontSize="sm" mb={1}>{campaign.name}</Text>
                {campaign.suggestions.map((suggestion) => {
                  const isBid = suggestion.type === "bid";
                  const difference = suggestion.newValue - suggestion.oldValue;
                  const isIncrease = difference > 0;
                  
                  return (
                    <Box 
                      key={suggestion.id}
                      py={1} 
                      px={2}
                      bg="white" 
                      borderRadius="md"
                      mt={1}
                      boxShadow="0 1px 3px rgba(0,0,0,0.12)"
                    >
                      <Flex justify="space-between" alignItems="center">
                        <HStack spacing={1} flex="1">
                          <Icon as={isBid ? FiTrendingUp : FiDollarSign} color="brand.500" boxSize={3} />
                          <Text fontSize="xs" fontWeight="medium">
                            {isBid ? "CPI" : "Budget"}
                          </Text>
                        </HStack>
                        
                        <HStack spacing={1} flex="2">
                          <Text fontSize="xs" color="gray.600">${suggestion.oldValue.toFixed(2)}</Text>
                          <Icon as={FiArrowRight} color="brand.400" boxSize={3} />
                          <Text fontSize="xs" color="brand.600" fontWeight="bold">${suggestion.newValue.toFixed(2)}</Text>
                          <Badge 
                            colorScheme={isIncrease ? "green" : "red"} 
                            fontSize="2xs"
                            variant="subtle"
                            px={1}
                            py={0}
                          >
                            {isIncrease ? "+" : ""}${Math.abs(difference).toFixed(2)}
                          </Badge>
                        </HStack>
                      </Flex>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </VStack>
        </Box>
        
        <Flex justifyContent="center" pt={2}>
          <Button 
            colorScheme="brand" 
            size="sm" 
            leftIcon={<Icon as={IoSparkles} boxSize={3} />} 
            onClick={handleApplyAll}
            isLoading={isOpening}
            loadingText="Applying..."
          >
            Apply All
          </Button>
        </Flex>
      </VStack>
      {ConfirmDialog}
    </Box>
  );
};

export default SuggestionsPreview; 